import { multiLineString, multiPolygon, point, feature, featureCollection } from '@turf/helpers';
import config from 'config';
import { groupBy } from 'lodash';
import { createSelector } from 'reselect';
import { SearchMapBulletinFields, SearchMapProjectFields } from 'store/sagas/apiService/types';
import {
  activePoiIdsSelector,
  savedPoiIdsSelector,
  visitedPoiIdsSelector,
} from 'store/state/searchContext/selectors';
import { AppMode, appModeSelector, routeSelector, routeNameSelector, routeFixedMultiPreviewSelector } from 'store/state/selectors/router';
import { currentPoiPointSelector, searchListDataLayersPoiListSelector, searchDataLayersDictionarySelector, allSearchDataLayersDictionarySelector, relevantDocsSelector } from 'store/state/selectors/search';
import {
  CompletionType,
  IAddressDetails,
  IAutocompleteBorough,
  IAutocompleteCity,
  IAutocompleteEntry,
  IAutocompleteNeighbourhood,
  IAutocompleteStreet,
  IBulletin,
  ILatLngPoint,
  IProject,
  PoiId,
  PromotionValues,
  IDataLayerItem,
  ICommercialBulletin,
  ICommercialProject,
} from 'utils/entities';
import { IBulletinFeatureProps, IProjectFeatureProps, MapOwnProps, IDataLayerFeatureProps, ActiveAreaFeatureProps, ActiveStreetFeatureProps, ISchoolFeatureProps } from './types';
import { getId, getDistanceInMeters, genImageNameById } from './utils';
import { getRelevancy, UnitRelevancy } from 'components/unit-page/RelevancyLabel';
import { State } from 'store/state';
import { lessThanM, priceGrid } from 'locale/formatMetric';
import {
  mapPoiByIdsSelector,
  mapPoiListSelector,
  MapListingsPoi,
  unitPageBulletinSelector,
  unitPageCommercialBulletinSelector,
  projectPageProjectSelector,
  projectPageCommercialProjectSelector,
  soldPageDealSelector,
  searchListPoisDictionarySelector,
  addressDocEntrySelector,
} from 'store/state/domainData/selectors';
import { FeatureDescriptor } from 'utils/insightsFeatureTypes';
import { hoveredDataLayerIdSelector } from 'store/state/insightsContext/selectors';
import { Route } from 'config/routes';
import { WithLocaleProps, INumberToMetricStringFormatter, INumberToStringFormatter } from 'locale';
import { getFetureCentroid } from 'utils/geo';
import { mapMultiPreviewPoiListSelector } from 'store/state/domainData/selectors/mapPreview';
import { getEntryName } from 'utils/texts';
import {
  projectMapLogoDictionarySelector,
} from 'store/state/domainData/selectors/projectMapLogo';
import { lastHoveredBusLineDataLayerItemSelector } from './DataLayersPreview/selectors';
import { schoolByIdSelector } from 'store/state/domainData/selectors/school';

const getAddress = <T extends { addressDetails: IAddressDetails }>({ addressDetails }: T) => addressDetails.docId;
const moneyFormatterWithMetricPrefixSelector = (_: State, { formatMoneyWithMetricPrefix }: MapOwnProps | WithLocaleProps) => formatMoneyWithMetricPrefix;
const moneyFormatterSelector = (_: State, { formatMoney }: MapOwnProps | WithLocaleProps) => formatMoney;
const locationToTuple = ({ lng, lat }: ILatLngPoint): [ number, number ] => [ lng, lat ];
const tupleToLocation = ([ lng, lat ]: [ number, number ]): ILatLngPoint => ({ lng, lat });
const isProject = (item: Pick<IBulletin, SearchMapBulletinFields> | Pick<IProject, SearchMapProjectFields> | Pick<ICommercialBulletin, SearchMapBulletinFields> | Pick<ICommercialProject, SearchMapProjectFields>) => item.type === 'project' || item.type === 'commercialProject';
const isBulletin = (item: Pick<IBulletin, SearchMapBulletinFields> | Pick<IProject, SearchMapProjectFields> | Pick<ICommercialBulletin, SearchMapBulletinFields> | Pick<ICommercialProject, SearchMapProjectFields>) => item.type === 'bulletin' || item.type === 'commercialBulletin';

const mapAndListPoiByIdsSelector = createSelector([
  mapPoiByIdsSelector,
  searchListPoisDictionarySelector,
], (map, list) => ({ ...list, ...map }));

const projectsListSelector = createSelector([
  mapPoiListSelector,
], (poi) => poi.filter(isProject) as IProject[]);

const bulletinsListSelector = createSelector([
  mapAndListPoiByIdsSelector,
], (poiDictionary) => Object.values(poiDictionary).filter(isBulletin));

const groupedBulletinsSelector = createSelector([
  bulletinsListSelector,
], (bulletins: IBulletin[]) =>
  groupBy<Pick<IBulletin, SearchMapBulletinFields> | Pick<ICommercialBulletin, SearchMapBulletinFields>>(bulletins, getAddress));

const activeBulletinsGroupsSelector = createSelector([
  activePoiIdsSelector,
  groupedBulletinsSelector,
  mapAndListPoiByIdsSelector,
  mapMultiPreviewPoiListSelector,
  routeFixedMultiPreviewSelector,
], (activeIds, groupedBulletins, bulletinsByids, fixedMultiBulletins, fixedMultiPoiIds) => {
  const bulletinsGroup: Array<Array<Pick<IBulletin, SearchMapBulletinFields> | Pick<ICommercialBulletin, SearchMapBulletinFields>>> = [];
  let shouldDiscardActiveIds = false;

  if (fixedMultiPoiIds && fixedMultiBulletins && fixedMultiBulletins.length) {
    const [ oneOfActive ] = fixedMultiBulletins;

    if (activeIds.has(oneOfActive.id)) {
      shouldDiscardActiveIds = true;
    }

    if (oneOfActive) {
      const hash = getAddress(oneOfActive);
      bulletinsGroup.push(groupedBulletins[hash]);
    }
  }

  if (activeIds.size && !shouldDiscardActiveIds) {
    const activeBulletins = [ ...activeIds ].map((id) => bulletinsByids[id]);
    const [ oneOfActive ] = activeBulletins;

    if (oneOfActive && (oneOfActive.type === 'bulletin' || oneOfActive.type === 'commercialBulletin')) {
      const hash = getAddress(oneOfActive);
      bulletinsGroup.push(groupedBulletins[hash]);
    }
  }

  return bulletinsGroup;
});


export const activePoiCoordsSelector = createSelector([
  mapAndListPoiByIdsSelector,
  activePoiIdsSelector,
], (poiByIds, activePoiIds): [ number, number ] => {
  const [ poiId ] = activePoiIds;
  const poi = poiByIds[poiId];
  return poi ? locationToTuple(poi.locationPoint) : null;
});

export const activeMultiPoiCoordsSelector = createSelector([
  mapAndListPoiByIdsSelector,
  routeFixedMultiPreviewSelector,
], (poiByIds, fixedMultiPreviewIds): [ number, number ] => {
  if (!fixedMultiPreviewIds) return null;

  const [ poiId ] = fixedMultiPreviewIds;
  const poi = poiByIds[poiId];
  return poi ? locationToTuple(poi.locationPoint) : null;
});


const makeBulletinProperties = (group: MapListingsPoi[], isActive: boolean, moneyFormatter: INumberToStringFormatter | INumberToMetricStringFormatter, visited: Set<PoiId>, saved: Set<PoiId>): IBulletinFeatureProps => {
  const isMulti = group.length > 1;
  const [ poi ] = group;
  return {
    featureType: 'targetPoint',
    isNew: group.some((b) => getRelevancy({ createdDate: b.firstTimeSeen, isInLastViewed: false, isBulletinType: true }) === UnitRelevancy.New),
    type: 'bulletin',
    ids: group.map(getId),
    groupLength: group.length,
    isAgent: (poi as Pick<IBulletin, SearchMapBulletinFields>).poc.type === 'agent',
    price: (isMulti || poi.type === 'project' || poi.type === 'commercialProject')
      ? null
      : lessThanM(poi.price) ? (moneyFormatter as INumberToMetricStringFormatter)(poi.price, 6, priceGrid.m) : moneyFormatter(poi.price),
    isActive,
    isVisited: group.every(({ id }) => visited.has(id)),
    isSaved: isMulti ? null : saved.has(poi.id),
  };
};

const makeProjectProperties = (project: Pick<IProject, SearchMapProjectFields> | Pick<ICommercialProject, SearchMapProjectFields>, isActive: boolean, visited: Set<PoiId>, saved: Set<PoiId>, projectLogo?: string): IProjectFeatureProps => {
  const isPromoted = project.promotionStatus.status === PromotionValues.Promoted;
  const hasProjectLogo = Boolean(projectLogo);
  return {
    type: 'project',
    featureType: isPromoted ? 'promotedTarget' : 'projectTarget',
    id: project.id,
    ids: [ project.id ],
    groupLength: 1,
    isPromoted,
    isActive,
    hasProjectLogo,
    imageName: hasProjectLogo ? genImageNameById(project.id, projectLogo) : null,
    activeImageName: hasProjectLogo ? genImageNameById(project.id, projectLogo, true) : null,
    isVisited: visited.has(project.id),
    isSaved: saved.has(project.id),
    isNew: getRelevancy({ createdDate: project.firstTimeSeen, isInLastViewed: false, isBulletinType: false }) === UnitRelevancy.New,
  };
};

export const makeFeature = <T>(locationPoint: ILatLngPoint, properties: T): GeoJSON.Feature<GeoJSON.Point, T> => ({
  type: 'Feature',
  geometry: {
    type: 'Point',
    coordinates: locationToTuple(locationPoint),
  },
  properties,
});

const isMultiPoly = (address: IAutocompleteEntry): address is IAutocompleteBorough | IAutocompleteNeighbourhood | IAutocompleteCity => (
  'geometry' in address && address.geometry.length > 0
);
const isLineString = (address: IAutocompleteEntry): address is IAutocompleteStreet => (
  'lineString' in address && address.lineString.length > 0
);

export const activeSchoolGeoJSONSelector = createSelector([
  relevantDocsSelector,
], (searchDocs): GeoJSON.FeatureCollection<GeoJSON.Point, IDataLayerFeatureProps> => {
  const features = searchDocs.map((searchDoc): GeoJSON.Feature<GeoJSON.Point, IDataLayerFeatureProps> => {
    if (searchDoc && searchDoc.type === CompletionType.SchoolDoc) {
      const properties: IDataLayerFeatureProps = {
        isNeed: false,
        isActive: true,
        type: 'school',
        featureType: FeatureDescriptor.TargetPoint,
        id: searchDoc.docId,
      };
      return point(searchDoc.location, properties);
    }
    return null;
  }).filter(Boolean);

  return featureCollection(features);
});

export const activeAreaGeoJSONSelector = createSelector([
  relevantDocsSelector,
], (searchDocs): GeoJSON.FeatureCollection<GeoJSON.MultiPolygon, ActiveAreaFeatureProps> => {
  const features = searchDocs.map((searchDoc): GeoJSON.Feature<GeoJSON.MultiPolygon, ActiveAreaFeatureProps> => {

    if (searchDoc && searchDoc.type === CompletionType.SchoolDoc && searchDoc.zoneGeometry && searchDoc.zoneGeometry.length && !searchDoc.virtualSchoolGeo) {
      return multiPolygon(searchDoc.zoneGeometry, {
        name: getEntryName(searchDoc),
        featureType: FeatureDescriptor.ZoneGeometry,
      });
    }
    if (searchDoc
      && isMultiPoly(searchDoc)
      && !(searchDoc.type === CompletionType.City && !config.drawCityGeometry)
    ) {
      return multiPolygon(searchDoc.geometry, {
        name: getEntryName(searchDoc),
        featureType: FeatureDescriptor.SearchArea,
      });
    }

    return null;
  }).filter(Boolean);

  return featureCollection(features);

});

export const activeStreetGeoJSONSelector = createSelector([
  relevantDocsSelector,
], (searchDocs): GeoJSON.FeatureCollection<GeoJSON.MultiLineString, ActiveStreetFeatureProps> => {
  const features = searchDocs.map((searchDoc): GeoJSON.Feature<GeoJSON.MultiLineString, ActiveStreetFeatureProps> => (
    searchDoc && isLineString(searchDoc)
      ? multiLineString(searchDoc.lineString, {
        name: searchDoc.street,
        featureType: FeatureDescriptor.ActiveStreet,
      })
      : null
  )).filter(Boolean);

  return featureCollection(features);
});

const activeSoldPageSelector = createSelector([
  soldPageDealSelector,
  routeSelector,
], (soldPage, route) => {
  return route.name === Route.Sold ? soldPage : null;
});

const activeSchoolPageSelector = createSelector([
  schoolByIdSelector,
  routeSelector,
], (schoolPage, route) => {
  return route.name === Route.School ? schoolPage : null;
});

export const activeBuildingGeoJSONSelector = createSelector([
  addressDocEntrySelector,
  routeNameSelector,
  activeSoldPageSelector,
], (resolvedAddress, route, soldPage): GeoJSON.Feature<GeoJSON.Point, { featureType: 'targetPoint' }> => {

  if (soldPage && soldPage.locationPoint) return null;

  return resolvedAddress && resolvedAddress.type === CompletionType.Address && route !== Route.Search
    ? point(resolvedAddress.location, {
      featureType: 'targetPoint',
    })
    : null;
});

const emptyPoisCollection: GeoJSON.FeatureCollection<GeoJSON.Point, IBulletinFeatureProps | IProjectFeatureProps> = {
  type: 'FeatureCollection',
  features: [],
};

const activeListingPageSelector = createSelector([
  unitPageBulletinSelector,
  unitPageCommercialBulletinSelector,
  projectPageProjectSelector,
  projectPageCommercialProjectSelector,
  routeSelector,
], (unitPage = null, unitPageCommercial = null, projectPage = null, projectPageCommercial = null, route) => {
  switch (route.name) {
    case Route.UnitPage:
      return unitPage;
    case Route.UnitPageCommercial:
      return unitPageCommercial;
    case Route.ProjectPage:
      return projectPage;
    case Route.ProjectPageCommercial:
      return projectPageCommercial;
  }

  return null;
});

interface IDealFeatureProps {
  featureType: 'targetPoint';
  type: 'bulletin';
}

const dealFeatureProps: IDealFeatureProps = {
  featureType: 'targetPoint',
  type: 'bulletin',
};

export const currentPoiGeoJSONSelector = createSelector([
  activeListingPageSelector,
  visitedPoiIdsSelector,
  savedPoiIdsSelector,
  activeSoldPageSelector,
  moneyFormatterSelector,
  activeSchoolPageSelector,
], (activeListing, visitedPois, savedPois, soldPage, moneyFormatter, schoolPage): GeoJSON.FeatureCollection<GeoJSON.Point, IBulletinFeatureProps | IProjectFeatureProps | IDealFeatureProps | ISchoolFeatureProps> => {
  if (activeListing) {
    const location = activeListing.locationPoint;
    const props = activeListing.type === 'project'
      ? makeProjectProperties(activeListing, true, visitedPois, savedPois)
      : makeBulletinProperties([ activeListing ], true, moneyFormatter, visitedPois, savedPois);
    return  {
      type: 'FeatureCollection',
      features: [ makeFeature(location, props) ],
    };
  }

  if (schoolPage && schoolPage.geometry && schoolPage.geometry.coordinates) {
    return {
      type: 'FeatureCollection',
      features: [ makeFeature(tupleToLocation(schoolPage.geometry.coordinates), { featureType: 'targetPoint', type: 'school' }) ],
    };
  }

  if (soldPage && soldPage.locationPoint) {
    return {
      type: 'FeatureCollection',
      features: [ makeFeature(soldPage.locationPoint, dealFeatureProps) ],
    };
  }

  return emptyPoisCollection;
});

export const makeActivePoiGeoJSONSelector = () => createSelector([
  activeBulletinsGroupsSelector,
  visitedPoiIdsSelector,
  savedPoiIdsSelector,
  moneyFormatterSelector,
  activePoiIdsSelector,
  mapAndListPoiByIdsSelector,
  moneyFormatterWithMetricPrefixSelector,
  appModeSelector,
  projectMapLogoDictionarySelector,
], (
  activeGroups,
  visitedPois,
  savedPois,
  moneyFormatter,
  activePoiIds,
  poiByIds,
  moneyFormatterWithMetricPrefix,
  appMode,
  projectLogosDict
): GeoJSON.FeatureCollection<GeoJSON.Point, IBulletinFeatureProps | IProjectFeatureProps> => {
  let properties: IProjectFeatureProps | IBulletinFeatureProps = null;
  let location: ILatLngPoint = null;

  const features: Array<GeoJSON.Feature<GeoJSON.Point, IBulletinFeatureProps | IProjectFeatureProps>> = [];

  if (!activeGroups.length) {
    if (activePoiIds.size) {
      const [ projectId ] = activePoiIds;
      const project = poiByIds[projectId];
      // defensive check
      if (project && (project.type === 'project' || project.type === 'commercialProject')) {
        const projectLogo = projectLogosDict[project.id] && projectLogosDict[project.id].logo && projectLogosDict[project.id].logo.title || null;
        properties = makeProjectProperties(project, true, visitedPois, savedPois, projectLogo);
        location = project.locationPoint;
        if (location && properties) {
          features.push(makeFeature(location, properties));
        }
      }
    }
  }
  else {
    activeGroups.map(activeGroup => {
      const [ oneOfActive ] = activeGroup;
      const formatter = appMode === AppMode.Rent ? moneyFormatter : moneyFormatterWithMetricPrefix;

      properties = makeBulletinProperties(activeGroup, true, formatter, visitedPois, savedPois);
      location = oneOfActive.locationPoint;

      if (location && properties) {
        features.push(makeFeature(location, properties));
      }
    });
  }

  if (features.length) {
    return {
      type: 'FeatureCollection',
      features,
    };
  }

  return emptyPoisCollection;
});

const NEAREST_DATA_LAYER_THRESHOLD = 200;

function mapDataLayerToFeatureProps(
  poi: IDataLayerItem,
  moneyFormatter: INumberToMetricStringFormatter,
  isActive: boolean
): IDataLayerFeatureProps {
  const properties: IDataLayerFeatureProps = {
    isActive,
    isNeed: false,
    type: poi.type,
    featureType: FeatureDescriptor.TargetPoint,
    id: poi.id,
  };

  switch (poi.type) {
    case 'deal': {
      const { price, groupLength } = poi;
      Object.assign(properties, {
        groupLength,
        price: moneyFormatter(price, 2, 1e6),
        latestId: poi.latestId,
      });
      break;
    }
    case 'school':
      Object.assign(properties, {
        name: poi.name,
        isNeed: properties.isNeed && poi.schoolScore >= 4,
      });
      break;
    case 'dogPark':
    case 'park':
    case 'trainStation':
      Object.assign(properties, {
        name: poi.name,
      });
      break;
    case 'busStation':
      Object.assign(properties, {
        name: poi.busStopName,
      });
      break;
    case 'area':
      Object.assign(properties, {
        name: poi.name,
        areaType: poi.areaType,
      });
      break;
  }

  return properties;
}

const nearbyActiveNeedsSelector = createSelector([
  searchListDataLayersPoiListSelector,
  currentPoiPointSelector,
  activePoiCoordsSelector,
], (dataLayersPoi, currentPoiPoint, activePoiCoords): string[] => {
  if (!dataLayersPoi.length || !(activePoiCoords || currentPoiPoint)) return [];
  const activeNeeds = dataLayersPoi.filter(dataLayerItem => {
    const props = mapDataLayerToFeatureProps(dataLayerItem, () => null, null);
    if (props.isNeed) {
      const itemCoords = getFetureCentroid(dataLayerItem.geometry);
      const listingCoords = activePoiCoords || currentPoiPoint;
      if (itemCoords && listingCoords) {
        const distanceInMeters = getDistanceInMeters(listingCoords, itemCoords);
        return distanceInMeters <= NEAREST_DATA_LAYER_THRESHOLD;
      }
    }
    return false;
  }).map(item => item.id);
  return activeNeeds;
});


function mapDataLayersToFeatures(items: IDataLayerItem[], moneyFormatter: INumberToMetricStringFormatter, isActive: boolean) {
  const features = items.map((poi): GeoJSON.Feature<GeoJSON.Geometry, IDataLayerFeatureProps> => {
    const properties = mapDataLayerToFeatureProps(poi, moneyFormatter, isActive);
    return feature(poi.geometry.geometry, properties);
  });

  return featureCollection(features);
}

export const dataLayersPoiGeoJSONSelector = createSelector([
  searchListDataLayersPoiListSelector,
  moneyFormatterWithMetricPrefixSelector,
], (dataLayersPoi, moneyFormatter): GeoJSON.FeatureCollection<GeoJSON.Geometry, IDataLayerFeatureProps> => (
  mapDataLayersToFeatures(dataLayersPoi, moneyFormatter, false)
));

export const activeDataLayersPoiGeoJSONSelector = createSelector([
  searchDataLayersDictionarySelector,
  allSearchDataLayersDictionarySelector,
  hoveredDataLayerIdSelector,
  nearbyActiveNeedsSelector,
  moneyFormatterWithMetricPrefixSelector,
  lastHoveredBusLineDataLayerItemSelector,
], (dataLayersDict, dataLayersBackupDictionary, hoveredDataLayerId, nearbyActiveNeeds, formatter, lastHoveredBusLayer): GeoJSON.FeatureCollection<GeoJSON.Geometry, IDataLayerFeatureProps> => {

  const pois = [ (lastHoveredBusLayer && lastHoveredBusLayer.id), hoveredDataLayerId, ...nearbyActiveNeeds ].map((id) => dataLayersDict[id] || dataLayersBackupDictionary[id]).filter(Boolean);

  return mapDataLayersToFeatures(pois, formatter, true);
});

export const poiGeoJSONSelector = createSelector([
  groupedBulletinsSelector,
  projectsListSelector,
  savedPoiIdsSelector,
  visitedPoiIdsSelector,
  moneyFormatterSelector,
  appModeSelector,
  moneyFormatterWithMetricPrefixSelector,
  projectMapLogoDictionarySelector,
], (grouped, projects, savedBulletins, visitedBulletins, moneyFormatter, appMode, moneyFormatterWithMetricPrefix, projectLogosDict): GeoJSON.FeatureCollection<GeoJSON.Point, IBulletinFeatureProps | IProjectFeatureProps> => {
  const bulletinsFeatures = appMode === AppMode.Check ? [] : Object.values(grouped).map((poiGroup) => {

    const [ poi ] = poiGroup;
    const formatter = appMode === AppMode.Rent ? moneyFormatter : moneyFormatterWithMetricPrefix;

    const properties = makeBulletinProperties(poiGroup, false, formatter, visitedBulletins, savedBulletins);

    return makeFeature(poi.locationPoint, properties);
  }, []);

  const nonRromotedProjectsFeatures = appMode === AppMode.Check ? [] : projects.filter(project => project.promotionStatus.status !== PromotionValues.Promoted).map((project) => {

    const projectLogo = projectLogosDict[project.id] && projectLogosDict[project.id].logo && projectLogosDict[project.id].logo.title || null;
    const properties = makeProjectProperties(project, false, visitedBulletins, savedBulletins, projectLogo);

    return makeFeature(project.locationPoint, properties);
  }, []);

  return {
    type: 'FeatureCollection',
    features: [ ...bulletinsFeatures.reverse(), ...nonRromotedProjectsFeatures.reverse() ],
  };
});

export const promotedProjectGeoJSONSelector = createSelector([
  projectsListSelector,
  savedPoiIdsSelector,
  visitedPoiIdsSelector,
  appModeSelector,
  projectMapLogoDictionarySelector,
], (projects, savedProjects, visitedProjects, appMode, projectLogosDict): GeoJSON.FeatureCollection<GeoJSON.Point, IProjectFeatureProps> => {
  const features = appMode === AppMode.Check ? [] : projects.filter(project => project.promotionStatus.status === PromotionValues.Promoted).map((project) => {
    const projectLogo = projectLogosDict[project.id] && projectLogosDict[project.id].logo && projectLogosDict[project.id].logo.title || null;
    const properties = makeProjectProperties(project, false, visitedProjects, savedProjects, projectLogo);

    return makeFeature(project.locationPoint, properties);
  }, []);

  return {
    type: 'FeatureCollection',
    features: features.reverse(),
  };
});
